import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { API } from '@/api';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';

type StaffReservation =
  API['GET']['StaffReservationSearch']['response']['reservations'][number];

interface StaffOnlyConfirmDeleteDialogProps {
  onClose: () => void;
  onConfirm: () => void;
  reservations: StaffReservation[];
  selectedIds: Set<string>;
  isDeleting: boolean;
}

export const StaffOnlyConfirmDeleteDialog = forwardRef<
  HTMLDialogElement,
  StaffOnlyConfirmDeleteDialogProps
>(({ onClose, onConfirm, reservations, selectedIds, isDeleting }, ref) => {
  const { t } = useTranslation();
  const { formatReservationTimeRangeStaffOnly } = useDateTimeFormat();

  return (
    <dialog className="dialog dialog--confirm" ref={ref}>
      <div className="dialog__scroll">
        <div className="dialog__line">
          <div className="dialog__area">
            {/* 選択された予約のリスト表示 */}
            <div className="dialog__text">
              {Array.from(selectedIds).map((reservationNo) => {
                const reservation = reservations.find(
                  (r) => r.reservation_no === reservationNo,
                );
                if (!reservation) return null;

                return (
                  <div className="dialog-reservation" key={reservationNo}>
                    {/* 名前・メール */}
                    <div className="dialog-reservation__row dialog-reservation__row--user">
                      <div className="dialog-reservation__name">
                        {reservation.last_name} {reservation.first_name}
                      </div>
                      <div className="dialog-reservation__email">
                        {reservation.email}
                      </div>
                    </div>

                    {/* 予約番号 */}
                    <div className="dialog-reservation__row dialog-reservation__row--no">
                      {reservationNo}
                    </div>

                    {/* 日時 */}
                    <div className="dialog-reservation__row dialog-reservation__row--datetime">
                      {(() => {
                        const [date, timeRange] =
                          formatReservationTimeRangeStaffOnly(
                            reservation.starts_at,
                            reservation.ends_at,
                          );
                        return (
                          <>
                            <div>{date}</div>
                            <div>{timeRange}</div>
                          </>
                        );
                      })()}
                    </div>

                    {/* 施設 */}
                    <div className="dialog-reservation__row dialog-reservation__row--facility">
                      <div>{t(`common.airport.${reservation.airport}`)}</div>
                      <div>{t(`common.terminal.${reservation.terminal}`)}</div>
                      <div>
                        {t(`common.flightType.${reservation.flight_type}`)}
                      </div>
                      <div>{t(`common.lounge.${reservation.lounge}`)}</div>
                      <div>
                        {t('staffOnly.confirmDialog.spaceNo')}{' '}
                        {reservation.space_no ?? '-'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 確認メッセージ */}
            <div className="dialog__message">
              <p className="dialog__message-line">
                {t('staffOnly.confirmDialog.message')}
              </p>
            </div>

            {/* ボタン */}
            <div className="dialog__actions">
              <button
                type="button"
                onClick={onConfirm}
                disabled={isDeleting}
                className="dialog__btn dialog__btn--delete"
              >
                {t('staffOnly.confirmDialog.confirmButton')}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isDeleting}
                className="dialog__btn dialog__btn--no"
              >
                {t('staffOnly.confirmDialog.cancelButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  );
});

StaffOnlyConfirmDeleteDialog.displayName = 'StaffOnlyConfirmDeleteDialog';
